import { Side } from '@/compiled_proto/com/celertech/marketdata/api/enums/SideProto';
import { useAppSelector } from '@/state/hooks';
import { selectTradingSettings } from '@/state/reducers/settingSlice';
import { useDebouncedOneClickTrading } from '@/utils/hooks/useDebouncedOneClickTrading';
import useLanguage from '@/utils/hooks/useLanguage';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdCheckCircle, MdLock } from 'react-icons/md';
import Tooltip from '../../common/Tooltip';

interface FormSubmitProps {
    side: Side;
    isValid: boolean;
    disabled: boolean;
    className?: string;
    noSettlementDate?: boolean;
}

// put this in window.config
const expirationDuration = 5000;

const FormSubmit = ({ side, isValid, disabled, className = '', noSettlementDate = false }: FormSubmitProps) => {
    const lang = useLanguage();
    const trading = useAppSelector(selectTradingSettings);
    const isTwoClickTrading = trading?.twoClickTrading?.value || false;

    const [locked, setLocked] = useState<boolean>(!!isTwoClickTrading);
    const [twoClickExpirationTimer, setTwoClickExpirationTimer] = useState<number>(expirationDuration);

    const onDebouncedOneClick = useDebouncedOneClickTrading(setLocked, 300);

    const tooltip = useMemo(() => {
        if (!isValid) {
            return lang.orderForm.pleaseEnsureAllInputsAreValid;
        } else {
            if (noSettlementDate) {
                return lang.orderForm.noSettlementDateAvailable;
            } else if (locked && isTwoClickTrading) {
                return lang.orderForm.clickOnceToEnableButton;
            } else if (!locked) {
                return lang.orderForm.clickToPlaceOrder;
            }
        }
    }, [lang, isValid, noSettlementDate, locked]);

    const onOneClickHandler = useCallback(
        (event) => {
            onDebouncedOneClick(false);
            if (!locked) {
                setLocked(true);
            } else {
                event.preventDefault();
            }
        },
        [locked]
    );

    const onTwoClickHandler = useCallback(
        (event) => {
            if (locked) {
                setLocked(false);
                setTwoClickExpirationTimer(5000);
                event.preventDefault();
            } else {
                setLocked(true);
            }
        },
        [locked]
    );

    useEffect(() => {
        setLocked(!!isTwoClickTrading);
    }, [isTwoClickTrading]);

    useEffect(() => {
        if (!locked && isTwoClickTrading) {
            const timer = setTimeout(() => setTwoClickExpirationTimer(twoClickExpirationTimer - 1000), 1000);
            if (twoClickExpirationTimer <= 0) {
                clearTimeout(timer);
                setLocked(true);
            }
        }
    }, [locked, isTwoClickTrading, twoClickExpirationTimer]);

    return (
        <Tooltip content={tooltip} className="max-w-fit">
            <button
                type="submit"
                // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
                className={cn(
                    'w-full flex justify-center items-center py-2 px-4 rounded-md shadow-sm',
                    'text-sm font-medium focus:outline-none focus:ring-2 transition-colors uppercase',
                    {
                        [`bg-brand-green hover:bg-brand-green-light focus:ring-brand-green text-brand-neutral-800`]:
                            !(disabled || noSettlementDate) && !locked && side === Side.BUY,
                        [`bg-brand-red hover:bg-brand-red-light focus:ring-brand-red text-brand-neutral-800`]:
                            !(disabled || noSettlementDate) && !locked && side !== Side.BUY,
                        [`border border-brand-green hover:border-brand-green-light text-brand-text`]:
                            !(disabled || noSettlementDate) && locked && side === Side.BUY,
                        [`border border-brand-red hover:border-brand-red-light text-brand-text`]:
                            !(disabled || noSettlementDate) && locked && side !== Side.BUY,
                        'bg-brand-neutral-700 hover:bg-brand-neutral-600 focus:ring-brand-neutral-600 cursor-not-allowed':
                            disabled || noSettlementDate
                    }
                )}
                onClick={(e) => {
                    if (!isTwoClickTrading) onOneClickHandler(e);
                    else onTwoClickHandler(e);
                }}
                disabled={disabled || noSettlementDate}>
                {isTwoClickTrading && (
                    <span
                        className={cn('absolute', {
                            'left-4': !className,
                            [className]: className
                        })}>
                        {locked || !isValid ? <MdLock className="w-4 h-4" /> : <MdCheckCircle className="w-5 h-5" />}
                    </span>
                )}
                <span>{lang.orderForm[Side[side].toLowerCase()]}</span>
            </button>
        </Tooltip>
    );
};

export default FormSubmit;
